import {View} from '@fantasmagames/slot/src/common/view/View';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {Meter} from '@fantasmagames/core/src/update/Meter';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';
import {WinMeter} from '@fantasmagames/slot/src/modules/winpresentation/WinMeter';
import {WinFieldConstants} from 'src/winfield/WinFieldConstants';
import {BackgroundView} from '@fantasmagames/slot/src/modules/background/BackgroundView';
import {WinFieldWinNumbers} from 'src/winfield/WinFieldWinNumbers';
import {WinFieldWinMeter} from 'src/winfield/WinFieldWinMeter';

export class WinFieldView extends View {
	protected _winField: PIXI.Sprite;
	protected _meter: Meter;

	protected _currentWinNumber: number = 0;
	protected _winNumberText: WinNumbers;

	public init(): void {
		super.init();

		this.setupWinField();
		this.setupWinNumberText();
		this.createMeter();

		this.container.addChild(this._winField);
	}

	protected setupWinField(): void {
		this._winField = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'WIN_PRESENTATION.png'));
		const pos: Position = this.layout.getAbsolutePosition('WIN_FIELD');
		this._winField.position.set(pos.x, pos.y);
		this._winField.anchor.set(0.5);

		const winText: PIXI.Sprite = new PIXI.Sprite(this.resources.getLocaleTextImage('WIN_FIELD_TEXT.png'));
		if (this.device.isMobile) {
			winText.anchor.set(0.5);
		} else {
			winText.anchor.set(1, 0.5);
		}
		const winTextPos: Position = this.layout.getPosition('WIN_FIELD_TEXT');
		winText.position.set(winTextPos.x, winTextPos.y);
		this._winField.addChild(winText);
	}

	protected setupWinNumberText(): void {
		const winNumberFont = WinFieldConstants.WIN_NUMBER_FONT_NAME;
		const numberSprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('NUMBERS.json', `${winNumberFont}0.png`));
		const spriteWidth = numberSprite.width;

		if (this.device.isMobile) {
			this._winNumberText = new WinNumbers(this.resources, winNumberFont);
		} else {
			this._winNumberText = new WinFieldWinNumbers(this.resources, winNumberFont);
		}
		this._winNumberText.currencyIDPrefix = WinFieldConstants.CURRENCY_FONT_NAME;

		this._winNumberText.currencyISO = this.currencyFormatter.currency;
		this._winNumberText.currencySign = this.currencyFormatter.currencySign;

		this._winNumberText.numberAnchor = {x: 0.0, y: 0.5};
		this.adjustCurrencyOffset();
		this._winNumberText.numberKerning = -10;

		const winTextNumberPos: Position = this.layout.getPosition('WIN_FIELD_NUMBER');

		this._winNumberText.position.set(winTextNumberPos.x - spriteWidth * WinFieldConstants.SCALE_TO_FIT_BOX / 2, winTextNumberPos.y);
		this._winField.addChild(this._winNumberText);
	}

	protected adjustCurrencyOffset(): void {
		const currency = this.currencyFormatter.currency;

		if (currency === 'EUR' || currency === 'GBP' || currency === 'MYR' || currency === 'THB'
			|| currency === 'JPY' || currency === 'BOB' || currency === 'CAD' || currency === 'CHF'
			|| currency === 'CNY' || currency === 'COP' || currency === 'ARS' || currency === 'AUD'
			|| currency === 'DKK' || currency === 'GHS' || currency === 'HKD' || currency === 'ILS'
			|| currency === 'KES' || currency === 'MMK' || currency === 'MXN' || currency === 'NOK'
			|| currency === 'NZD' || currency === 'PEN' || currency === 'PHP' || currency === 'RWF'
			|| currency === 'SGD' || currency === 'TWD' || currency === 'ZAR') {
			this._winNumberText.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'PKR') {
			this._winNumberText.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'PLN' || currency === 'SEK') {
			this._winNumberText.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'CZK' || currency === 'HRK' || currency === 'HUF' || currency === 'RUB') {
			this._winNumberText.currencyOffset = {x: 0, y: 0};
		} else {
			this._winNumberText.currencyOffset = {x: 0, y: 15};
		}
	}

	protected createMeter(): void {
		this._meter = new WinFieldWinMeter(this._winNumberText,
			Easing.Linear.None, this.currencyFormatter, true, WinFieldConstants.SCALE_TO_FIT_BOX);
		this._meter.set(0);
	}

	public scaleInWinValue(): void {
		const scalingTween: Tween = new Tween(this._winNumberText.scale)
			.to({x: WinFieldConstants.SCALING_TO_VALUE, y: WinFieldConstants.SCALING_TO_VALUE}, 250)
			.start();
		this.updateManager.add(scalingTween);
	}

	public scaleOutWinValue(): void {
		const scalingTween: Tween = new Tween(this._winNumberText.scale)
			.to({x: WinFieldConstants.SCALE_TO_FIT_BOX, y: WinFieldConstants.SCALE_TO_FIT_BOX}, 250)
			.start();
		this.updateManager.add(scalingTween);
	}

	public fadeOutWinValue(): void {
		const fadingTween: Tween = new Tween(this._winNumberText).to({alpha: 0}, 500).start();
		this.updateManager.add(fadingTween);
	}

	public fadeInWinValue(): void {
		this._winNumberText.alpha = 1;
	}

	public zeroWin(): void {
		this._meter.set(0);
		this._currentWinNumber = 0;
	}

	public countup(toValue: number, time: number): void {
		this._meter.stop();
		this._meter = null;

		this.scaleInWinValue();

		this._meter = new WinFieldWinMeter(this._winNumberText,
			Easing.Linear.None, this.currencyFormatter, true, WinFieldConstants.SCALE_TO_FIT_BOX);
		this._meter.from(this._currentWinNumber).to(toValue, time).start();
		this._meter.onCompleteCallback = this.scaleOutWinValue.bind(this);

		this.updateManager.add(this._meter);

		this._currentWinNumber = toValue;
	}

	public setReelsContainerAlphaToZero(): void {
		this.container.alpha = 0;
	}

	public playReelContainerTransition(): void {
		const appearTween: Tween = new Tween(this.container)
			.to({alpha: 1.0}, BackgroundView.TRANSFER_TIME)
			.start();

		this.updateManager.add(appearTween);
	}
}



// WEBPACK FOOTER //
// ./src/winfield/WinFieldView.ts